import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { settingService } from '../../services/api';
import { getProductImageUrl } from '../../utils/imageHelper';
import styles from './Cart.module.css';

const Cart = () => {
  const { items, totalItems, isLoading, updateQuantity, removeFromCart, clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { addNotification } = useNotifications();
  const [settings, setSettings] = useState({
    delivery_fee: 500,
    tax_rate: 0.18,
    tax_enabled: true,
    free_delivery_threshold: 50000,
  });

  // Charger les settings au montage
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await settingService.getPublicSettings();
        if (response.success && response.data) {
          // Normaliser le taux de TVA : si > 1, diviser par 100 (ex: 10 -> 0.10)
          let taxRate = response.data.tax_rate || 0.18;
          if (taxRate > 1) {
            taxRate = taxRate / 100;
          }

          // Normaliser tax_enabled en boolean
          let taxEnabled = true;
          if (response.data.tax_enabled !== undefined && response.data.tax_enabled !== null) {
            taxEnabled = response.data.tax_enabled === true ||
                        response.data.tax_enabled === 'true' ||
                        response.data.tax_enabled === '1' ||
                        response.data.tax_enabled === 1;
          }

          setSettings({
            // Utiliser les valeurs de la DB même si elles sont 0
            delivery_fee: response.data.delivery_fee !== undefined && response.data.delivery_fee !== null ? response.data.delivery_fee : 500,
            delivery_fee_per_km: response.data.delivery_fee_per_km !== undefined && response.data.delivery_fee_per_km !== null ? response.data.delivery_fee_per_km : 500,
            tax_rate: taxRate,
            tax_enabled: taxEnabled,
            free_delivery_threshold: response.data.free_delivery_threshold !== undefined && response.data.free_delivery_threshold !== null ? response.data.free_delivery_threshold : 50000,
          });
        }
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error);
      }
    };

    loadSettings();
  }, []);

  // Calculer le sous-total dynamiquement basé sur les items actuels
  // Utiliser le prix promotionnel si disponible
  const calculatedSubtotal = useMemo(() => {
    return items.reduce((sum, item) => {
      // Priorité: promotional_price > price > unit_price
      const price = parseFloat(
        item.promotional_price ||
        item.price ||
        item.unit_price ||
        (item.product?.promotional_price) ||
        (item.product?.sale_price) ||
        (item.product?.price) ||
        0
      );
      const quantity = item.quantity || 0;
      return sum + (price * quantity);
    }, 0);
  }, [items]);

  // Calculer le total avec frais de livraison et taxes
  const shippingCost = calculatedSubtotal >= settings.free_delivery_threshold ? 0 : settings.delivery_fee;
  const calculatedTax = settings.tax_enabled ? calculatedSubtotal * settings.tax_rate : 0;
  const calculatedTotal = calculatedSubtotal + shippingCost + calculatedTax;

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      const result = await removeFromCart(itemId);
      if (result && result.success) {
        addNotification('Produit retiré du panier', 'info');
      } else {
        addNotification(result?.error || 'Erreur lors de la suppression', 'error');
      }
    } else {
      const result = await updateQuantity(itemId, newQuantity);
      if (result && result.success) {
        addNotification(result.message || 'Quantité mise à jour', 'success');
      } else {
        addNotification(result?.error || 'Erreur lors de la mise à jour', 'error');
      }
    }
  };

  const handleRemoveItem = async (itemId) => {
    const result = await removeFromCart(itemId);
    if (result && result.success) {
      addNotification('Produit retiré du panier', 'info');
    } else {
      addNotification(result?.error || 'Erreur lors de la suppression', 'error');
    }
  };

  const handleClearCart = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir vider votre panier ?')) {
      const result = await clearCart();
      if (result && result.success) {
        addNotification('Panier vidé avec succès', 'info');
      }
    }
  };

  if (isLoading) {
    return (
      <div className={styles.cart}>
        <div className={styles.loading}>
          <p>Chargement du panier...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.cart}>
      <div className={styles.cartContainer}>
        <h1>Mon Panier</h1>

        {items.length === 0 ? (
          <div className={styles.emptyCart}>
            <h2>Votre panier est vide</h2>
            <p>Découvrez nos produits frais et ajoutez-les à votre panier.</p>
            <Link to="/" className={styles.continueShopping}>
              Continuer mes achats
            </Link>
          </div>
        ) : (
          <>
            <div className={styles.cartItems}>
              {items.map((item) => (
                <div key={item.id} className={styles.cartItem}>
                  <div className={styles.itemImage}>
                    <img
                      src={
                        item.product?.primary_image
                          ? getProductImageUrl(item.product.primary_image)
                          : (item.product?.images?.[0] ? getProductImageUrl(item.product.images[0]) : '/images/leaves-right.png')
                      }
                      alt={item.product_name || item.product?.name}
                      onError={(e) => {
                        e.target.src = '/images/leaves-right.png';
                      }}
                    />
                  </div>

                  <div className={styles.itemDetails}>
                    <h3>{item.product_name || item.product?.name}</h3>
                    <div className={styles.itemPriceContainer}>
                      {item.best_promotion && (
                        <span className={styles.promotionBadge}>
                          🎁 {item.best_promotion.name}
                        </span>
                      )}
                      <p className={styles.itemPrice}>
                        {parseFloat(
                          item.promotional_price ||
                          item.price ||
                          item.unit_price ||
                          (item.product?.promotional_price) ||
                          (item.product?.sale_price) ||
                          (item.product?.price) ||
                          0
                        ).toLocaleString('fr-FR')} FCFA
                      </p>
                      {(item.original_price || item.price) &&
                       (item.promotional_price || item.product?.promotional_price) &&
                       (item.original_price || item.price) > (item.promotional_price || item.product?.promotional_price) && (
                        <p className={styles.itemOldPrice}>
                          {parseFloat(item.original_price || item.price).toLocaleString('fr-FR')} FCFA
                        </p>
                      )}
                    </div>
                    <p className={styles.itemUnit}>
                      Par {item.unit || item.product?.unit || 'kg'}
                    </p>
                  </div>

                  <div className={styles.itemControls}>
                    <div className={styles.quantityControl}>
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        disabled={item.quantity >= (item.product?.stock_quantity || 99)}
                      >
                        +
                      </button>
                    </div>

                  <div className={styles.itemTotal}>
                      {((parseFloat(
                        item.promotional_price ||
                        item.price ||
                        item.unit_price ||
                        (item.product?.promotional_price) ||
                        (item.product?.sale_price) ||
                        (item.product?.price) ||
                        0
                      ) * (item.quantity || 0)).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }))} FCFA
                  </div>

                    <button
                      className={styles.removeButton}
                      onClick={() => handleRemoveItem(item.id)}
                      title="Supprimer"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.cartSummary}>
              <div className={styles.summaryRow}>
                <span>Sous-total ({totalItems} article{totalItems > 1 ? 's' : ''})</span>
                <span>{calculatedSubtotal.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA</span>
              </div>

              <div className={styles.summaryRow}>
                <span>Frais de livraison</span>
                <span>{shippingCost.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA</span>
              </div>

              {settings.tax_enabled && calculatedTax > 0 && (
                <div className={styles.summaryRow}>
                  <span>TVA ({(settings.tax_rate * 100).toFixed(0)}%)</span>
                  <span>{calculatedTax.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA</span>
                </div>
              )}

              <div className={styles.summaryTotal}>
                <span>Total</span>
                <span>{calculatedTotal.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA</span>
              </div>
            </div>

            <div className={styles.cartActions}>
              <button
                className={styles.clearCartButton}
                onClick={handleClearCart}
              >
                Vider le panier
              </button>

              <Link
                to="/"
                className={styles.continueShopping}
              >
                Continuer mes achats
              </Link>

              {isAuthenticated ? (
                <Link
                  to="/checkout"
                  className={styles.checkoutButton}
                >
                  Passer commande
                </Link>
              ) : (
                <Link
                  to="/login"
                  className={styles.loginButton}
                >
                  Se connecter pour commander
                </Link>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Cart;












